/**
 * MapHoverPopup.js
 * ---------------------------------------------------------------
 * Portal-rendered tooltip that appears when hovering over a map
 * feature. Displays bar charts of survey values, sample sizes,
 * benchmark averages, and optional compare-wave deltas.
 *
 * Extracted from MapPage to keep the main render method lean.
 * All data dependencies are passed explicitly as props.
 */
import React from "react";
import ReactDOM from "react-dom";
import { buildLabelsAndColors } from "./utils/mapPage/helpers";
import { DEMO_LABELS } from "./utils/demographicLabels";
import { weightColForGroup, weightedMean } from "./utils/keyWeights";

/**
 * @param {object}   hoverInfo                  - {x, y, props} from onMouseMove
 * @param {string}   geoLevel                   - "constituency" | "ward" | "local_authority"
 * @param {object}   group                      - active group (from getActiveGroup)
 * @param {object}   groups                     - all groupsById
 * @param {array}    themeColours               - colour palette
 * @param {string}   actionMode                 - "explore" | "filter"
 * @param {string}   preferenceFilter           - e.g. "net", "top"
 * @param {object}   actionTargetSubVariables   - {TargetSubVariable1, ...}
 * @param {object}   filters                    - {gender, age, ethnicity, ...}
 * @param {array}    metricRows                 - current level metric rows
 * @param {array}    metricRowsUnfiltered       - unfiltered metric rows for benchmarks
 * @param {array}    metricRowsConstituencies   - national constituency baseline
 * @param {string}   selectedConstituencyCode   - currently drilled-into constituency
 * @param {object}   selectedDemographics       - display labels for active filters
 * @param {object}   mapRef                     - ref to the ReactMap instance
 * @param {function} normalizeAreaIdForLevel    - ID normalisation helper
 * @param {function} getLatestTwoWaves          - returns [wave1, wave2] or null
 * @param {boolean}  compareWaves               - compare-wave mode active flag
 * @param {array}    compareWavePair            - [latestWave, prevWave] or null
 * @param {array}    compareRowsLatest          - metric rows for latest wave
 * @param {array}    compareMetricRows          - delta metric rows
 * @param {Set|null} newThisWaveKeys            - keys with no prior wave, so their
 *                                                whole current share is the movement
 * @param {number|string|null} waveSelect       - selected tracking wave for benchmark rows
 */
export default function MapHoverPopup({
  hoverInfo,
  geoLevel,
  group,
  groups,
  themeColours,
  actionMode,
  preferenceFilter,
  actionTargetSubVariables,
  filters,
  metricRows,
  metricRowsUnfiltered: metricRowsUnfilteredProp,
  metricRowsConstituencies,
  selectedConstituencyCode,
  selectedDemographics,
  mapRef,
  normalizeAreaIdForLevel,
  getLatestTwoWaves,
  // Compare-wave props (inactive in current UK deployment, kept for future use)
  compareWaves = null,
  compareWavePair: compareWavePairProp = null,
  compareRowsLatest = null,
  compareMetricRows = null,
  newThisWaveKeys = null,
  waveSelect = null,
  compareKey = null,
}) {
  if (!hoverInfo) return null;

  const hProps = hoverInfo.props || {};

  // ── Area name ────────────────────────────────────────────────
  const isZip = geoLevel === "zipcodes";
  const areaName = isZip && hProps.GEOID20
    ? `ZIP ${hProps.GEOID20}`
    : hProps.NAME || hProps.name || hProps.WD24NM || hProps.PCON24NM || hProps.LAD23NM || "Unknown Area";
  const areaSub = isZip && hProps.NAME ? hProps.NAME : null;

  // ── Parse stored bar items from feature properties ────────────
  let items = hProps._allValuesSorted;
  if (typeof items === "string") { try { items = JSON.parse(items); } catch { items = null; } }
  if (!Array.isArray(items)) {
    let byKey = hProps._allValuesByKey;
    if (typeof byKey === "string") { try { byKey = JSON.parse(byKey); } catch { byKey = null; } }
    if (byKey && typeof byKey === "object") {
      items = Object.entries(byKey).map(([key, value]) => ({ key, label: key, value }));
    } else {
      let obj = hProps._allValues;
      if (typeof obj === "string") { try { obj = JSON.parse(obj); } catch { obj = null; } }
      items = obj && typeof obj === "object"
        ? Object.entries(obj).map(([key, value]) => ({ key, label: key, value }))
        : [];
    }
  }

  // Append computed net score if group has polarity
  let byKeyForNet = hProps._allValuesByKey;
  if (typeof byKeyForNet === "string") { try { byKeyForNet = JSON.parse(byKeyForNet); } catch { byKeyForNet = null; } }
  if (byKeyForNet && typeof byKeyForNet === "object" && group?.polarity) {
    const toArrNet = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const sumKeysNet = (keys) => keys.reduce((acc, k) => acc + (Number(byKeyForNet?.[k]) || 0), 0);
    const pos = sumKeysNet(toArrNet(group.polarity.pos));
    const neg = sumKeysNet(toArrNet(group.polarity.neg));
    const netVal = Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
    if (Number.isFinite(netVal)) {
      items = [...items, { key: "net1", label: "Net", value: netVal }];
    }
  }

  // ── Colours and formatting ────────────────────────────────────
  const { colors } = buildLabelsAndColors(groups || {}, themeColours || []);
  const isPrice  = group?.type === "price_elast" || group?.TargetVariable1?.type === "price_elast";
  // likert_grid in compare mode: group is the parent, items are sector net scores
  const isLikertGridCompare = group?.type === "likert_grid";
  const isLikertGridTarget = actionMode === "filter" && group?.TargetVariable1?.type === "likert_grid";
  const [targetSubVarId, targetMetricRaw] = String(actionTargetSubVariables?.TargetSubVariable1 || "").split("|");
  const targetSubGroup = isLikertGridTarget ? groups?.[targetSubVarId] : null;
  const toArrMetric = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const firstPos = toArrMetric(targetSubGroup?.polarity?.pos)[0];
  const firstNeg = toArrMetric(targetSubGroup?.polarity?.neg)[0];
  const posText = String(targetSubGroup?.labels?.[firstPos] || "").toLowerCase();
  const negText = String(targetSubGroup?.labels?.[firstNeg] || "").toLowerCase();
  const usesComfortLabels = `${posText} ${negText}`.includes("comfort");
  const sentimentPosLabel = usesComfortLabels ? "Comfort" : "Trust";
  const sentimentNegLabel = usesComfortLabels ? "Discomfort" : "Distrust";
  const metricToken = String(targetMetricRaw || "trust").toLowerCase();
  const targetMetricMode = ["distrust", "discomfort", "neg", "negative"].includes(metricToken)
    ? "neg"
    : ["trust", "comfort", "pos", "positive"].includes(metricToken)
      ? "pos"
      : metricToken;
  const fmtVal   = (v) => isPrice ? `$${(v / 100).toFixed(2)}` : `${v.toFixed(1)}%`;
  const fmtBench = fmtVal;
  const clampPct = (v) => Math.max(0, Math.min(100, v));

  // ── Visible bars ──────────────────────────────────────────────
  const inExplore = actionMode === "explore";
  const baseItems = (() => {
    if (!inExplore) return items;
    if (isLikertGridCompare) return items; // sector nets can be negative — show every sector (14, not 13: the cap silently dropped Gambling)
    if (group?.type === "likert") {
      // Keep likert responses in sentiment order, not "top issue" ranking.
      const byKey = new Map((items || []).map((it) => [String(it?.key), it]));
      const ordered = [];
      const pushKeys = (vals) => {
        (Array.isArray(vals) ? vals : vals != null ? [vals] : []).forEach((k) => {
          const it = byKey.get(String(k));
          if (it && Number.isFinite(Number(it.value))) ordered.push(it);
        });
      };
      pushKeys(group?.polarity?.pos);
      pushKeys(group?.polarity?.neu);
      pushKeys(group?.polarity?.neg);
      pushKeys(group?.polarity?.dk);
      // Fallback for any keys not covered by polarity.
      (group?.keys || []).forEach((k) => {
        const it = byKey.get(String(k));
        if (it && Number.isFinite(Number(it.value)) && !ordered.some((x) => String(x.key) === String(k))) {
          ordered.push(it);
        }
      });
      return ordered;
    }
    return items.filter((it) => Number(it?.value) > 0).slice(0, 10);
  })();
  const visibleBars = (() => {
    if (isLikertGridTarget) {
      const byKey = new Map(baseItems.map((it) => [it.key, Number(it.value)]));
      const trustKeys = toArrMetric(targetSubGroup?.polarity?.pos);
      const distrustKeys = toArrMetric(targetSubGroup?.polarity?.neg);
      const sum = (keys) =>
        keys.reduce((acc, k) => acc + (Number.isFinite(byKey.get(k)) ? byKey.get(k) : 0), 0);
      const trustCombined = sum(trustKeys);
      const distrustCombined = sum(distrustKeys);
      // A raw response key targeted (e.g. windfall "Uncomfortable"): show that
      // response's own bar, not the positive-sentiment default.
      if (targetSubGroup?.keys?.includes(targetMetricRaw)) {
        const v = byKey.get(targetMetricRaw);
        return Number.isFinite(v)
          ? [{ key: targetMetricRaw, label: targetSubGroup?.labels?.[targetMetricRaw] || targetMetricRaw, value: v }]
          : [];
      }
      if (targetMetricMode === "net") {
        // Net targeting: one bar, the net score itself.
        return Number.isFinite(trustCombined) && Number.isFinite(distrustCombined)
          ? [{ key: "net1", label: "Net", value: trustCombined - distrustCombined }]
          : [];
      }
      const primary = targetMetricMode === "neg"
        ? { key: "distrust_combined", label: sentimentNegLabel, value: distrustCombined }
        : { key: "trust_combined", label: sentimentPosLabel, value: trustCombined };
      const bars = [primary];
      if (Number.isFinite(trustCombined) && Number.isFinite(distrustCombined)) {
        bars.push({ key: "net1", label: "Target net", value: trustCombined - distrustCombined });
      }
      return bars.filter((b) => Number.isFinite(Number(b.value)));
    }

    const filtered = baseItems.filter(({ key, value }) => {
      if (!Number.isFinite(Number(value))) return false;
      if (inExplore) return true;
      if (actionMode === "filter") {
        const t = actionTargetSubVariables?.TargetSubVariable1;
        return key === t || (t === "net" && key === "net1");
      }
      return false;
    });

    if (inExplore && group?.type === "categorical") {
      // Specific key selected — show only that key, looked up in the FULL item
      // list: baseItems is pre-sliced to the top-10 positive values, so
      // low-share options (e.g. Gambling on sector importance) never survived
      // it and hovered as "No data after filters".
      if (preferenceFilter && Array.isArray(group?.keys) && group.keys.includes(preferenceFilter)) {
        const it = (items || []).find((x) => String(x?.key) === String(preferenceFilter));
        return it && Number.isFinite(Number(it.value)) ? [it] : [];
      }
      // top/second/third or default — show top 6
      return filtered.slice(0, 6);
    }

    return filtered;
  })();
  // Rank of a specifically-selected categorical key (1 = highest value in this area)
  const selectedCategoricalKeyRank = (() => {
    if (!inExplore || group?.type !== "categorical" || compareWaves) return null;
    if (!preferenceFilter || !Array.isArray(group?.keys) || !group.keys.includes(preferenceFilter)) return null;
    const allSorted = (items || [])
      .filter((it) => Number.isFinite(Number(it?.value)) && it?.key !== "net1" && it?.key !== "Net")
      .sort((a, b) => Number(b.value) - Number(a.value));
    const idx = allSorted.findIndex((it) => it.key === preferenceFilter);
    return idx >= 0 ? idx + 1 : null;
  })();

  const likertFocusMode = inExplore
    && !compareWaves
    && (group?.type === "likert" || group?.type === "categorical")
    && (
      preferenceFilter === "top"
      || (preferenceFilter && Array.isArray(group?.keys) && group.keys.includes(preferenceFilter))
    );
  const focusedLikertKey = (() => {
    if (!likertFocusMode) return null;
    if (preferenceFilter && Array.isArray(group?.keys) && group.keys.includes(preferenceFilter)) {
      return preferenceFilter;
    }
    // "Top option": focus the strongest non-DK response for this hovered area.
    const dkSet = new Set(Array.isArray(group?.polarity?.dk) ? group.polarity.dk : (group?.polarity?.dk ? [group.polarity.dk] : []));
    const ranked = (visibleBars || [])
      .filter((b) => b && Array.isArray(group?.keys) && group.keys.includes(b.key) && !dkSet.has(b.key))
      .map((b) => ({ key: b.key, v: Number(b.value) }))
      .filter((x) => Number.isFinite(x.v))
      .sort((a, b) => b.v - a.v);
    return ranked[0]?.key || null;
  })();
  const hiddenNetValue = (() => {
    const netItem = (items || []).find((it) => it?.key === "net1" || it?.key === "Net");
    const netNum = Number(netItem?.value);
    return Number.isFinite(netNum) ? netNum : null;
  })();

  // ── Active sub-variable ───────────────────────────────────────
  const subVar = inExplore
    ? isLikertGridCompare
      ? null // compare mode: no single sub-variable, skip benchmarks
      : (group?.type === "likert" && (!preferenceFilter || preferenceFilter === "net") ? "net"
          : (preferenceFilter && group?.keys?.includes(preferenceFilter) ? preferenceFilter : group?.keys?.[0]))
    : (isLikertGridTarget ? targetMetricMode : actionTargetSubVariables?.TargetSubVariable1);

  // ── Benchmark helpers ─────────────────────────────────────────
  const toArr   = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const sumKeys = (keys, row) => keys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);

  const hasFilters = Object.values(filters || {}).some((v) => v != null);

  const calc = (row) => {
    if (!row || !group || !subVar) return null;
    if (isLikertGridTarget) {
      const posKeys = toArrMetric(targetSubGroup?.polarity?.pos);
      const negKeys = toArrMetric(targetSubGroup?.polarity?.neg);
      const trustVal = sumKeys(posKeys, row);
      const distrustVal = sumKeys(negKeys, row);
      if (subVar === "pos") return Number.isFinite(trustVal) ? trustVal : null;
      if (subVar === "neg") return Number.isFinite(distrustVal) ? distrustVal : null;
      if (subVar === "net") {
        const hasData = [...posKeys, ...negKeys].some((k) => Number.isFinite(Number(row?.[k])));
        if (!hasData) return null;
        return trustVal - distrustVal;
      }
    }
    if (subVar === "net") {
      const posKeys = toArr(inExplore ? group?.polarity?.pos : group?.TargetVariable1?.polarity?.pos);
      const negKeys = toArr(inExplore ? group?.polarity?.neg : group?.TargetVariable1?.polarity?.neg);
      const hasData = [...posKeys, ...negKeys].some((k) => Number.isFinite(Number(row?.[k])));
      if (!hasData) return null;
      const pos = sumKeys(posKeys, row);
      const neg = sumKeys(negKeys, row);
      return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
    }
    const v = Number(row?.[subVar]);
    return Number.isFinite(v) ? v : null;
  };

  const filterRowsForWave = (rows) => {
    if (waveSelect == null) return rows || [];
    return (rows || []).filter((r) => Number(r?.Wave) === Number(waveSelect));
  };

  // Build a lookup map for unfiltered rows by any area ID
  const metricRowsUnfiltered = filterRowsForWave(metricRowsUnfilteredProp || metricRows || []);
  const unfiltMap = new Map();
  metricRowsUnfiltered.forEach((r) => {
    [r?.area_id, r?.area_cd, r?.PCON24CD, r?.WD24CD]
      .filter(Boolean)
      .forEach((k) => { if (!unfiltMap.has(String(k))) unfiltMap.set(String(k), r); });
  });

  const areaId  = String(hProps.area_id ?? "");
  const pconId  = hProps.PCON24CD ? String(hProps.PCON24CD) : null;
  const wardId  = hProps.WD24CD ? String(hProps.WD24CD) : null;
  const unfiltRow = unfiltMap.get(areaId)
    || (pconId ? unfiltMap.get(pconId) : null)
    || (wardId ? unfiltMap.get(wardId) : null);

  // Benchmark values
  let areaBench = calc(unfiltRow);
  let filteredBench = null;
  if (hasFilters) {
    const fv = subVar === "net" ? Number(hProps.net1) : Number(hProps[subVar]);
    filteredBench = Number.isFinite(fv) ? fv : null;
  }

  // Sample sizes
  const hoverNFromRow   = Number(unfiltRow?.n_raked ?? unfiltRow?.total_sum ?? unfiltRow?.weight ?? unfiltRow?.n);
  const hoverNFromProps = Number(hProps.n_raked ?? hProps.total_sum ?? hProps.weight ?? hProps.n);
  const hoverN    = Number.isFinite(hoverNFromRow) ? hoverNFromRow : hoverNFromProps;
  const filteredN = hasFilters ? hoverNFromProps : null;

  // Constituency row (for ward-level constituency benchmarks)
  const consCode = hProps.PCON24CD || selectedConstituencyCode;
  const constituencyRows = filterRowsForWave(metricRowsConstituencies || []);
  const consRow  = consCode
    ? constituencyRows.find((r) =>
        String(r?.PCON24CD ?? r?.area_id ?? r?.area_cd ?? "") === String(consCode))
    : null;
  const consN = Number(consRow?.n_raked ?? consRow?.total_sum ?? consRow?.weight ?? consRow?.n);

  // ── Demographic filter pills ──────────────────────────────────
  const demoPills = [];
  if (selectedDemographics) {
    Object.entries(selectedDemographics).forEach(([key, val]) => {
      const lbls = Array.isArray(val) ? val : val ? [val] : [];
      lbls.forEach((l) => demoPills.push({
        key: `${key}-${l}`,
        text: `${key.charAt(0).toUpperCase() + key.slice(1)}: ${l}`,
      }));
    });
  }

  // ── n stats row ───────────────────────────────────────────────
  const nStats = [
    actionMode === "filter" && Number.isFinite(hoverN) && {
      label: geoLevel === "ward" ? "n (ward)" : "n",
      value: Math.round(hoverN).toLocaleString(),
      blue: false,
    },
    actionMode === "filter" && geoLevel === "ward" && Number.isFinite(consN) && {
      label: "n (constituency)",
      value: Math.round(consN).toLocaleString(),
      blue: false,
    },
    actionMode === "filter" && Number.isFinite(filteredN) && hasFilters && {
      label: "n (filtered)",
      value: Math.round(filteredN).toLocaleString(),
      blue: true,
    },
  ].filter(Boolean);

  // ── National weighted average ─────────────────────────────────
  // calc() reads one group's keys (a single response, or a pos−neg net), so
  // the whole benchmark shares that group's weight column — n_voters for
  // vote-share questions, n_raked otherwise. Same basis as the sidebar's
  // national figure, so the two agree. See utils/keyWeights.js.
  const nationalRows = Array.isArray(metricRowsConstituencies)
    ? constituencyRows
    : metricRowsUnfiltered;
  const nationalBench =
    nationalRows.length && group && subVar
      ? weightedMean(nationalRows, calc, weightColForGroup(group))
      : null;

  const consBench = geoLevel === "ward" ? calc(consRow) : null;

  const showBench = subVar === "net" || actionMode === "filter";
  const benchStats = [
    showBench && Number.isFinite(nationalBench) && { label: "national avg", value: fmtBench(nationalBench), blue: false },
    showBench && geoLevel === "ward" && Number.isFinite(consBench) && { label: "constituency avg", value: fmtBench(consBench), blue: false },
    showBench && hasFilters && Number.isFinite(areaBench) && { label: geoLevel === "ward" ? "ward avg" : "area avg", value: fmtBench(areaBench), blue: false },
    actionMode === "filter" && Number.isFinite(filteredBench) && { label: "filtered", value: fmtBench(filteredBench), blue: true },
  ].filter(Boolean);

  const hasFooter = nStats.length > 0 || benchStats.length > 0 || demoPills.length > 0;

  // ── Compare-wave deltas (inactive in current deployment) ──────
  const deltaWavePair = compareWavePairProp
    || getLatestTwoWaves?.(metricRows)
    || getLatestTwoWaves?.(compareRowsLatest);
  const isCompare = !!compareWaves && Array.isArray(deltaWavePair) && deltaWavePair.length === 2;

  const compareLatestRow = isCompare
    ? (() => {
        const normId = normalizeAreaIdForLevel?.(geoLevel, areaId);
        return (compareRowsLatest || []).find((r) =>
          normalizeAreaIdForLevel?.(geoLevel, r?.area_id) === normId ||
          String(r?.area_id ?? "") === areaId
        ) || null;
      })()
    : null;

  const compareDeltaRow = isCompare
    ? (() => {
        const normId = normalizeAreaIdForLevel?.(geoLevel, areaId);
        return (compareMetricRows || []).find((r) =>
          String(r?.area_id ?? "") === normId || String(r?.area_id ?? "") === areaId
        ) || null;
      })()
    : null;

  const groupForCompare = isCompare ? group : null;
  const toArrC  = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const sumKeysC = (keys, row) => keys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);

  const netDelta = isCompare && compareDeltaRow && groupForCompare
    ? (() => {
        const posKeys = toArrC(actionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.pos : groupForCompare?.polarity?.pos);
        const negKeys = toArrC(actionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.neg : groupForCompare?.polarity?.neg);
        if (!posKeys.length && !negKeys.length) return null;
        const pos = sumKeysC(posKeys, compareDeltaRow);
        const neg = sumKeysC(negKeys, compareDeltaRow);
        return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
      })()
    : null;

  // Deltas render at one decimal place, so anything under 0.05pp shows as
  // "0.0pp". Treat that as no movement: "biggest gainer" must list actual
  // gainers, never a flat or falling option padding the list out to three.
  // Matches the 0.05 threshold used for gainer/loser styling in Sidebar.js.
  const MOVE_EPS = 0.05;
  // An option with no previous wave hasn't grown by its share, it has appeared.
  // Label it rather than dressing it up as a like-for-like rise.
  const isNewThisWave = (k) => !!newThisWaveKeys?.has?.(k);

  // ── Compare-mode top gainer / top loser (computed once, used in chips + bar highlight) ──
  const { compareTopGainerKey, compareTopLoserKey } = (() => {
    if (!isCompare || !compareDeltaRow) return { compareTopGainerKey: null, compareTopLoserKey: null };
    const toArrLG = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    let topGainer = null, topLoser = null;
    if (isLikertGridCompare) {
      (group?.subVariables || []).forEach((sv) => {
        const subGroup = groups?.[sv];
        if (!subGroup?.polarity) return;
        const posKeys = toArrLG(subGroup.polarity.pos);
        const negKeys = toArrLG(subGroup.polarity.neg);
        const d = posKeys.reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0)
                - negKeys.reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0);
        if (!Number.isFinite(d)) return;
        if (d > MOVE_EPS && (!topGainer || d > topGainer.delta)) topGainer = { key: sv, delta: d };
        if (d < -MOVE_EPS && (!topLoser  || d < topLoser.delta))  topLoser  = { key: sv, delta: d };
      });
    } else {
      (group?.keys || []).filter((k) => k !== "net1" && k !== "Net").forEach((k) => {
        const d = Number(compareDeltaRow[k]);
        if (!Number.isFinite(d)) return;
        if (d > MOVE_EPS && (!topGainer || d > topGainer.delta)) topGainer = { key: k, delta: d };
        if (d < -MOVE_EPS && (!topLoser  || d < topLoser.delta))  topLoser  = { key: k, delta: d };
      });
    }
    return { compareTopGainerKey: topGainer?.key ?? null, compareTopLoserKey: topLoser?.key ?? null };
  })();

  // Compare-waves + categorical: override bar set based on compareKey
  const renderVisibleBars = (() => {
    // Compare + likert_grid with a specific sector selected: show that sector's
    // net alone, looked up in the full item list.
    if (isCompare && inExplore && isLikertGridCompare && compareKey && (group?.subVariables || []).includes(compareKey)) {
      const it = (items || []).find((x) => String(x?.key) === String(compareKey));
      return it && Number.isFinite(Number(it.value)) ? [it] : [];
    }
    if (isCompare && inExplore && group?.type === "categorical") {
      // Look bars up in the FULL item list, not the top-10 baseItems slice —
      // low-share options (e.g. Gambling on sector importance) always fell
      // outside the slice, so selecting them rendered an empty tooltip.
      const allFiltered = (items || []).filter(({ value }) => Number.isFinite(Number(value)));
      const deltaKeys = Array.isArray(group?.keys) ? group.keys.filter(k => k !== "net1" && k !== "Net") : [];

      if (compareKey === "__top__") {
        // Biggest gainer mode: up to 3 genuine risers, most positive first.
        // Fewer than 3 is correct — padding with flat or falling options is
        // what made "biggest gainer" show 0.0pp entries.
        return deltaKeys
          .map(k => ({ key: k, delta: Number(compareDeltaRow?.[k]) }))
          .filter(x => Number.isFinite(x.delta) && x.delta > MOVE_EPS)
          .sort((a, b) => b.delta - a.delta)
          .slice(0, 3)
          .map(({ key }) => allFiltered.find(b => b.key === key))
          .filter(Boolean);
      }
      if (compareKey === "__top_neg__") {
        // Biggest loser mode: up to 3 genuine fallers, most negative first.
        return deltaKeys
          .map(k => ({ key: k, delta: Number(compareDeltaRow?.[k]) }))
          .filter(x => Number.isFinite(x.delta) && x.delta < -MOVE_EPS)
          .sort((a, b) => a.delta - b.delta)
          .slice(0, 3)
          .map(({ key }) => allFiltered.find(b => b.key === key))
          .filter(Boolean);
      }
      if (compareKey && compareKey !== "__top__" && compareKey !== "__top_neg__") {
        // By category: show only the selected key
        return allFiltered.filter(({ key }) => key === compareKey);
      }
      // Fallback: biggest gainer + biggest loser
      const compareKeys = new Set([compareTopGainerKey, compareTopLoserKey].filter(Boolean));
      if (compareKeys.size) return allFiltered.filter(({ key }) => compareKeys.has(key));
    }
    return visibleBars;
  })();

  // ── Popup positioning ─────────────────────────────────────────
  const mapCanvas = mapRef?.current?.getMap()?.getCanvas();
  const mapRect   = mapCanvas?.getBoundingClientRect() ?? { left: 0, top: 0 };
  const vx = (hoverInfo.x ?? 0) + mapRect.left;
  const vy = (hoverInfo.y ?? 0) + mapRect.top;
  const PW = 280, PH = 500, PAD = 20;
  const goRight = (window.innerWidth  - vx) >= PW + PAD;
  const goLeft  =  vx                        >= PW + PAD;
  const goBelow = (window.innerHeight - vy)  >= PH + PAD;
  const goAbove =  vy                        >= PH + PAD;
  const hDir = goRight ? "right" : goLeft ? "left" : (window.innerWidth  - vx) >= vx ? "right" : "left";
  const vDir = goBelow ? "below" : goAbove ? "above" : (window.innerHeight - vy) >= vy ? "below" : "above";
  const posStyle = {
    position: "fixed",
    zIndex: 99999,
    pointerEvents: "none",
    ...(vDir === "below" ? { top:    vy + 10 } : { bottom: window.innerHeight - vy + 10 }),
    ...(hDir === "right" ? { left:   vx + 10 } : { right:  window.innerWidth  - vx + 10 }),
  };

  // ── Shared stat-row renderer (n stats + bench stats) ──────────
  const renderStatRow = (stats) => (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 0" }}>
      {stats.map(({ label, value, blue }, i) => (
        <React.Fragment key={label}>
          {i > 0 && <div style={{ width: 1, height: 10, background: "#d1d5db", margin: "0 6px", flexShrink: 0 }} />}
          <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: blue ? "#2563eb" : "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {label}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: blue ? "#2563eb" : "#374151" }}>
              {value}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  // ── Net bar helper (shared between visibleBars and compare net) ─
  const renderNetBar = (displayNum, colorOverride = null) => {
    const barW = clampPct(Math.abs(displayNum));
    const fill = colorOverride || (displayNum >= 0 ? "#16a34a" : "#dc2626");
    return (
      <div style={{ height: 3, background: "#f0f2f7", borderRadius: 3, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", top: 0, height: "100%",
          left: displayNum >= 0 ? "50%" : `${50 - barW / 2}%`,
          width: `${barW / 2}%`,
          background: fill,
          borderRadius: 3,
        }} />
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────
  return ReactDOM.createPortal(
    <div style={posStyle}>
      <div style={{
        width: 260,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        background: "#ffffff",
        borderRadius: 10,
        boxShadow: "0 4px 20px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{ padding: "10px 13px 8px", borderBottom: "1px solid #f0f2f7" }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1d23", lineHeight: 1.3 }}>
            {areaName}
          </div>
          {areaSub && (
            <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>{areaSub}</div>
          )}
          {isCompare && Array.isArray(deltaWavePair) && (
            <div style={{
              marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4,
              background: "rgba(25,118,210,0.07)", border: "1px solid rgba(25,118,210,0.15)",
              borderRadius: 5, padding: "2px 7px", fontSize: 10, color: "#1976d2", fontWeight: 600,
            }}>
              <span style={{ opacity: 0.7 }}>Wave {deltaWavePair[1]}</span>
              <span style={{ fontSize: 9 }}>→</span>
              <span>Wave {deltaWavePair[0]}</span>
            </div>
          )}
          {demoPills.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {demoPills.map(({ key, text }) => (
                <div key={key} style={{
                  fontSize: 10, fontWeight: 600, color: "#2563eb",
                  background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.18)",
                  borderRadius: 4, padding: "1px 5px", lineHeight: 1.6,
                }}>
                  {text}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Compare mode: movement leader summary ── */}
        {isCompare && compareDeltaRow && (compareTopGainerKey || compareTopLoserKey) && (() => {
          const gainerLabel = isLikertGridCompare
            ? (group?.subLabels?.[compareTopGainerKey] || compareTopGainerKey)
            : (group?.labels?.[compareTopGainerKey] || compareTopGainerKey);
          const loserLabel = isLikertGridCompare
            ? (group?.subLabels?.[compareTopLoserKey] || compareTopLoserKey)
            : (group?.labels?.[compareTopLoserKey] || compareTopLoserKey);
          const gainerDelta = compareTopGainerKey
            ? (() => {
                if (isLikertGridCompare) {
                  const sg = groups?.[compareTopGainerKey];
                  const toArrLG = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
                  const pos = toArrLG(sg?.polarity?.pos).reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0);
                  const neg = toArrLG(sg?.polarity?.neg).reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0);
                  return pos - neg;
                }
                return Number(compareDeltaRow[compareTopGainerKey]);
              })()
            : null;
          const loserDelta = compareTopLoserKey
            ? (() => {
                if (isLikertGridCompare) {
                  const sg = groups?.[compareTopLoserKey];
                  const toArrLG = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
                  const pos = toArrLG(sg?.polarity?.pos).reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0);
                  const neg = toArrLG(sg?.polarity?.neg).reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0);
                  return pos - neg;
                }
                return Number(compareDeltaRow[compareTopLoserKey]);
              })()
            : null;
          return (
            <div style={{ padding: "5px 13px 6px", borderBottom: "1px solid #f0f2f7", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {compareTopGainerKey && Number.isFinite(gainerDelta) && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 4,
                  background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)",
                  borderRadius: 5, padding: "3px 7px", fontSize: 10.5, fontWeight: 600, color: "#15803d",
                }}>
                  <span style={{ fontSize: 11 }}>↑</span>
                  <span>{gainerLabel}</span>
                  <span style={{ opacity: 0.75 }}>
                    {isNewThisWave(compareTopGainerKey) ? `new · ${gainerDelta.toFixed(1)}%` : `+${gainerDelta.toFixed(1)}pp`}
                  </span>
                </div>
              )}
              {compareTopLoserKey && Number.isFinite(loserDelta) && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 4,
                  background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.18)",
                  borderRadius: 5, padding: "3px 7px", fontSize: 10.5, fontWeight: 600, color: "#b91c1c",
                }}>
                  <span style={{ fontSize: 11 }}>↓</span>
                  <span>{loserLabel}</span>
                  <span style={{ opacity: 0.75 }}>{loserDelta.toFixed(1)}pp</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Bar chart ── */}
        <div style={{ padding: "9px 13px", borderBottom: hasFooter ? "1px solid #f0f2f7" : "none" }}>
          {renderVisibleBars.length === 0 ? (
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              {isCompare && compareKey === "__top__"     ? "No positive movement here"
                : isCompare && compareKey === "__top_neg__" ? "No negative movement here"
                : items.some((it) => Number.isFinite(Number(it?.value))) ? "No data after filters"
                : "No data"}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: isLikertGridCompare ? 1 : 5 }}>
              {renderVisibleBars.map(({ key, label, value }) => {
                const num      = Number(value);
                const isNet    = key === "net1" || key === "Net";
                const toArrPol = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
                const isPolPos = !isNet && group?.polarity && toArrPol(group.polarity.pos).includes(key);
                const isPolNeg = !isNet && group?.polarity && toArrPol(group.polarity.neg).includes(key);
                const barColor = isLikertGridCompare
                  ? (group?.subColors?.[key.split(":")[1]] || group?.subColors?.[key] || "#6e6e6e")
                  : (isLikertGridTarget && !isNet)
                    ? ((targetMetricMode === "neg" || toArrPol(targetSubGroup?.polarity?.neg).includes(key)) ? "#dc2626" : "#16a34a")
                  : isNet
                    ? (num >= 0 ? "#16a34a" : "#dc2626")
                    : group?.colors?.[key]  // explicit color wins over polarity-derived green/red
                    ? group.colors[key]
                    : isPolPos ? "#16a34a"
                    : isPolNeg ? "#dc2626"
                    : (colors[key] || "#2563eb");
                const barW     = clampPct(num);

                const displayLabel = inExplore
                  ? label
                  : isNet
                    ? "Net"
                    : (label || "");
                const variableLabel = inExplore
                  ? null
                  : (isLikertGridTarget
                    ? (group?.TargetVariable1?.subLabels?.[targetSubVarId] || group?.TargetVariable1?.label || null)
                    : (group?.TargetVariable1?.label || null));
                const currentVal = isCompare && compareLatestRow
                  ? isNet
                    ? (() => {
                        const pol = inExplore ? group?.polarity : group?.TargetVariable1?.polarity;
                        if (!pol) return null;
                        const pos = toArrPol(pol.pos).reduce((a, k) => a + (Number(compareLatestRow[k]) || 0), 0);
                        const neg = toArrPol(pol.neg).reduce((a, k) => a + (Number(compareLatestRow[k]) || 0), 0);
                        return pos - neg;
                      })()
                    : (() => {
                        // likert_grid sector bars are keyed by sub-variable id,
                        // which isn't a column in the wave rows — derive the
                        // sector net from the sub-group's polarity keys instead.
                        const subPol = isLikertGridCompare ? groups?.[key]?.polarity : null;
                        if (subPol) {
                          const pos = toArrPol(subPol.pos).reduce((a, k) => a + (Number(compareLatestRow[k]) || 0), 0);
                          const neg = toArrPol(subPol.neg).reduce((a, k) => a + (Number(compareLatestRow[k]) || 0), 0);
                          return pos - neg;
                        }
                        return Number(compareLatestRow[key]);
                      })()
                  : null;
                const deltaVal = isCompare
                  ? isNet
                    ? netDelta
                    : (() => {
                        if (!compareDeltaRow) return num;
                        const subPol = isLikertGridCompare ? groups?.[key]?.polarity : null;
                        if (subPol) {
                          const pos = toArrPol(subPol.pos).reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0);
                          const neg = toArrPol(subPol.neg).reduce((a, k) => a + (Number(compareDeltaRow[k]) || 0), 0);
                          return pos - neg;
                        }
                        return Number(compareDeltaRow[key]);
                      })()
                  : null;
                const showDelta     = isCompare && Number.isFinite(deltaVal);
                const barDisplayNum = isCompare && Number.isFinite(currentVal) ? currentVal : num;
                const barDisplayW   = isCompare && Number.isFinite(currentVal) ? clampPct(currentVal) : barW;
                const baseN = Number.isFinite(filteredN) && hasFilters ? filteredN : hoverN;
                const responseN =
                  !isNet &&
                  !isLikertGridCompare &&
                  Number.isFinite(baseN) &&
                  Number.isFinite(barDisplayNum) &&
                  barDisplayNum >= 0
                    ? Math.round((baseN * barDisplayNum) / 100)
                    : null;

                const isFocusedLikertRow = likertFocusMode && key === focusedLikertKey;
                const isCompareGainer = false;
                const isCompareLooser = false;
                const isLikertType = group?.type === "likert";
                const explicitColor = isLikertType ? (group?.colors?.[key] || null) : null;
                const polRowColor = isLikertType
                  ? (explicitColor || (isPolPos ? "#16a34a" : isPolNeg ? "#dc2626" : null))
                  : null;
                const rowBg = isLikertGridCompare
                  ? (isCompareGainer ? "rgba(22,163,74,0.08)" : isCompareLooser ? "rgba(220,38,38,0.07)" : "transparent")
                  : isCompareGainer
                    ? "rgba(22,163,74,0.08)"
                    : isCompareLooser
                      ? "rgba(220,38,38,0.07)"
                      : likertFocusMode
                        ? (isFocusedLikertRow ? "rgba(37,99,235,0.08)" : "transparent")
                        : polRowColor
                          ? `${polRowColor}0d`  // 0d ≈ 5% opacity hex
                          : "transparent";
                const rowBorder = isLikertGridCompare
                  ? (isCompareGainer ? "rgba(22,163,74,0.2)" : isCompareLooser ? "rgba(220,38,38,0.18)" : "transparent")
                  : isCompareGainer
                    ? "rgba(22,163,74,0.2)"
                    : isCompareLooser
                      ? "rgba(220,38,38,0.18)"
                      : likertFocusMode
                        ? (isFocusedLikertRow ? "rgba(37,99,235,0.28)" : "transparent")
                        : polRowColor
                          ? `${polRowColor}33`  // 33 ≈ 20% opacity hex
                          : "transparent";

                return (
                  <div
                    key={key}
                    style={{
                      padding: isLikertGridCompare ? "1px 0" : "4px 6px",
                      borderRadius: 6,
                      background: rowBg,
                      border: `1px solid ${rowBorder}`,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: isLikertGridCompare ? 1 : 2, gap: 8 }}>
                      <span style={{ flex: 1, paddingRight: 8, minWidth: 0 }}>
                        {variableLabel && (
                          <div style={{
                            fontSize: 9,
                            color: "#9ca3af",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            marginBottom: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}>
                            {variableLabel}
                          </div>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {selectedCategoricalKeyRank != null && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, color: "#6366f1",
                              background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.25)",
                              borderRadius: 3, padding: "1px 4px", flexShrink: 0, lineHeight: 1.4,
                            }}>
                              #{selectedCategoricalKeyRank}
                            </span>
                          )}
                          <div style={{
                            fontSize: 11, color: "#4b5563", fontWeight: 600,
                            whiteSpace: "normal",
                            lineHeight: 1.25,
                          }}>
                            {displayLabel}
                          </div>
                        </div>
                      </span>
                      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, flexShrink: 0 }}>
                        <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                          <span style={{
                            fontSize: 12, fontWeight: 700,
                            color: (isNet || isLikertGridCompare)
                              ? (barDisplayNum >= 0 ? "#16a34a" : "#dc2626")
                              : (isLikertGridTarget
                                ? (targetMetricMode === "neg" ? "#dc2626" : "#16a34a")
                                : (isPolPos ? "#16a34a" : isPolNeg ? "#dc2626" : "#1a1d23")),
                          }}>
                            {fmtVal(barDisplayNum)}
                          </span>
                          {showDelta && (
                            <span style={{
                              fontSize: 11, fontWeight: 600,
                              color: deltaVal > 0 ? "#16a34a" : deltaVal < 0 ? "#dc2626" : "#9ca3af",
                              lineHeight: 1.4,
                            }}>
                              {isNewThisWave(key)
                                ? `new · ${deltaVal.toFixed(1)}%`
                                : `${deltaVal > 0 ? "+" : ""}${deltaVal.toFixed(1)}pp`}
                            </span>
                          )}
                        </span>
                        {actionMode === "filter" && Number.isFinite(responseN) && (
                          <span style={{ fontSize: 9, fontWeight: 700, color: "#2563eb", letterSpacing: "0.03em" }}>
                            N {responseN.toLocaleString()}
                          </span>
                        )}
                      </span>
                    </div>
                    {isNet || isLikertGridCompare ? renderNetBar(barDisplayNum, isLikertGridCompare ? barColor : null) : (
                      <div style={{ height: 3, background: "#f0f2f7", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{
                          height: "100%", width: `${Math.max(0, barDisplayW)}%`,
                          background: barColor, borderRadius: 3,
                          transition: "width 0.15s ease-out",
                        }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Net summary row (when net not in renderVisibleBars — e.g. negative net hidden by value>0 filter) ── */}
        {!renderVisibleBars.some((b) => b.key === "net1" || b.key === "Net") && (
          <div style={{ padding: "6px 13px 8px", borderBottom: hasFooter ? "1px solid #f0f2f7" : "none" }}>

            {isCompare && Number.isFinite(netDelta) && (() => {
              const latestNet = compareLatestRow && groupForCompare
                ? (() => {
                    const posKeys = toArrC(actionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.pos : groupForCompare?.polarity?.pos);
                    const negKeys = toArrC(actionMode === "filter" ? groupForCompare?.TargetVariable1?.polarity?.neg : groupForCompare?.polarity?.neg);
                    const pos = sumKeysC(posKeys, compareLatestRow);
                    const neg = sumKeysC(negKeys, compareLatestRow);
                    return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
                  })()
                : null;
              const displayNum = Number.isFinite(latestNet) ? latestNet : netDelta;
              return (
                <div key="net-compare">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                    <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 600 }}>Net</span>
                    <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                      {Number.isFinite(latestNet) && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: displayNum >= 0 ? "#16a34a" : "#dc2626" }}>
                          {fmtVal(displayNum)}
                        </span>
                      )}
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        color: netDelta > 0 ? "#16a34a" : netDelta < 0 ? "#dc2626" : "#9ca3af",
                      }}>
                        {netDelta > 0 ? "+" : ""}{netDelta.toFixed(1)}pp
                      </span>
                    </span>
                  </div>
                  {renderNetBar(displayNum)}
                </div>
              );
            })()}
            {!isCompare && Number.isFinite(hiddenNetValue) && (
              <div key="net-static">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 600 }}>Net</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: hiddenNetValue >= 0 ? "#16a34a" : "#dc2626" }}>
                    {fmtVal(hiddenNetValue)}
                  </span>
                </div>
                {renderNetBar(hiddenNetValue)}
              </div>
            )}
          </div>
        )}

        {/* ── Footer: sample sizes + benchmarks ── */}
        {hasFooter && (
          <div style={{ padding: "7px 13px 9px", background: "#fafbfc" }}>
            {nStats.length > 0 && (
              <div style={{ marginBottom: benchStats.length > 0 ? 6 : 0 }}>
                {renderStatRow(nStats)}
              </div>
            )}
            {benchStats.length > 0 && renderStatRow(benchStats)}
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}
